import {
  Avatar,
  Box,
  Divider,
  IconButton,
  InputBase,
  MenuItem,
  Popover,
  Typography,
} from "@mui/material";
import { Search, Clear, Check as CheckIcon } from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface User {
  _id: string;
  fullName: string;
  [key: string]: any;
}

interface AssigneePickerPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  users: User[];
  assignedUsers: User[];
  onAssigneeChange: (newAssignees: User[]) => void;
}

const AssigneePickerPopover = ({
  anchorEl,
  onClose,
  users,
  assignedUsers,
  onAssigneeChange,
}: AssigneePickerPopoverProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const currentAssignee = assignedUsers?.length > 0 ? assignedUsers[0] : null;

  // Filter users based on search query
  const filteredUsers = users.filter((user) =>
    user.fullName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Helper function to get initials from full name
  const getInitials = (fullName: string): string => {
    if (!fullName) return "";
    const nameParts = fullName.split(" ");
    if (nameParts.length === 1) {
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    const initials = nameParts
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
    return initials.length > 2 ? initials.substring(0, 2) : initials;
  };

  // Handle single select - select user, call onAssigneeChange, and close
  const handleSelectUser = (user: User) => {
    if (currentAssignee?._id === user._id) {
      // Clicking the already-assigned user deselects them
      onAssigneeChange([]);
    } else {
      onAssigneeChange([user]);
    }
    setSearchQuery("");
    onClose();
  };

  const handlePopoverClose = () => {
    setSearchQuery("");
    onClose();
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handlePopoverClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      PaperProps={{
        sx: { width: 300, maxHeight: 400, p: 1 },
      }}
    >
      <Box sx={{ p: 1, mb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "#f5f5f5",
            borderRadius: 1,
            p: 0.5,
            mb: 1,
          }}
        >
          <Search sx={{ color: "text.secondary", ml: 1, mr: 1 }} />
          <InputBase
            placeholder={t("Search people...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ ml: 1, flex: 1 }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
          {searchQuery && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSearchQuery("");
              }}
            >
              <Clear fontSize="small" />
            </IconButton>
          )}
        </Box>

        {currentAssignee && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("Assigned")}
            </Typography>
            <MenuItem
              onClick={() => handleSelectUser(currentAssignee)}
              sx={{
                borderRadius: 1,
                backgroundColor: "rgba(25, 118, 210, 0.08)",
                mb: 1,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    mr: 1,
                    bgcolor: "primary.main",
                  }}
                >
                  <Typography variant="subtitle2">
                    {getInitials(currentAssignee.fullName)}
                  </Typography>
                </Avatar>
                <Typography sx={{ flexGrow: 1 }}>
                  {currentAssignee.fullName}
                </Typography>
                <CheckIcon sx={{ fontSize: 18, color: "primary.main" }} />
              </Box>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {currentAssignee ? t("Suggestions") : t("People")}
        </Typography>

        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => {
            // Skip if user is the current assignee
            if (currentAssignee && user._id === currentAssignee._id) return null;

            return (
              <MenuItem
                key={user._id}
                onClick={() => handleSelectUser(user)}
                sx={{ borderRadius: 1 }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      mr: 1,
                      bgcolor: "primary.main",
                    }}
                  >
                    <Typography variant="subtitle2">
                      {getInitials(user.fullName)}
                    </Typography>
                  </Avatar>
                  <Typography sx={{ flexGrow: 1 }}>{user.fullName}</Typography>
                </Box>
              </MenuItem>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            {t("No matching users found")}
          </Typography>
        )}
      </Box>
    </Popover>
  );
};

export default AssigneePickerPopover;
